import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { zodResolver } from "@hookform/resolvers/zod"
import { Box } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

/*
 * Regular Page Declaration and Configuration
 */
export const models_crud = {
  title: "Model",
  route: "/settings/models/:id",
  component,
  group: "settings",
  resource: "models",
  icon: Box,
  action: "read",
} satisfies PageConfig

/*
  💡 The schema of the form. This can be declared once outside the component code.
*/
const FormSchema = z.object({
  title: z.string(),
  model: z.string(),
  provider: z.string(),
})

/*
  💡 The type of the form data.
*/
type FormData = z.infer<typeof FormSchema>



export function component() {

  /*  
    💡 Utility Hook: useDatabaseItem
  
    This hook manages the lifecycle of a single database item being created or edited.  
    It abstracts away the common logic needed to fetch, create, update, or remove an item,  
    and provides helper functions to streamline form handling and user feedback.
  
    The hook automatically detects the `id` from the URL (or whether the route ends with `"new"`)  
    to determine if it should create a new record or load/edit an existing one.
  */
  const {
    data,          // The loaded item data. Null/undefined if not yet fetched.
    id,            // The id of the item being edited. Returns null if no id is in the URL or it ends with "new".
    loading,       // Boolean flag indicating whether the item is being fetched from the database.
    submit,        // Creates or updates the item. Automatically detects if it's a new record or an update based on `id`.
    toastSuccess,  // Helper to show a success toast. Message automatically adapts for create vs. update.
    navigate,      // Function to navigate to another page. Same behavior as react-router's `useNavigate`.
    error,         // The error object, if any operation fails.
    create,        // Explicitly creates a new item in the database.
    remove,        // Removes the item from the database (only works if `id` is defined).
    update,        // Updates the item in the database (only works if `id` is defined).
  } = useDatabaseItem("models")


  // 💡 React Hook: useForm.
  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data,
    values: data,
  })

  async function onSubmit(data: FormData) {
    console.log("Submit: ", data)
    await submit(data)
    toastSuccess()
    navigate("/settings/models")
  }

  // 💡 This is a loader component to show in forms when loading data.
  if (loading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {/*
          💡 When a component is placed as a child of <PageTitle>, 
            it is automatically positioned in the top-right corner 
            to maintain a consistent page layout.
          */}
          <PageTitle>

            {/*
                The CRUDToolbar component provides a standardized set of actions 
                for managing items (Create, Read, Update, Delete).  
                - Supports autocomplete for the table name.  
                - Ensures consistency across pages by reusing the same toolbar design.  
                - Automatically navigates to the list page after a successful action.

                ⚠️ Important: The "Accept" action button is a submit button.  
                For it to work correctly, the CRUDToolbar must be placed inside a <form> element.
          */}
            <CRUDToolbar table="models" onSuccess={"/settings/models"} />

          </PageTitle>

          {/* 💡 CSS rule that adds vertical spacing between form fields */}
          <div className="rgrid">

            {/*
              💡 Defines a row in a 12-column CSS grid layout.  
                This serves as the container for one or more responsive columns.
            */}
            <div className="row">

              {/*
                💡 Defines the number of columns an element should span in the 12-column layout.  
                  Class names go from `rc-1` to `rc-12`.  
                  The system also applies responsive rules automatically for smaller screens.
                */}
              <div className="rc-4">
                {/*
                  💡 Form fields are implemented using the standard Shadcn FormField components.  
                    These ensure consistent styling, validation handling, and accessibility.
                */}
                <FormField control={form.control} name="provider" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Provider</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a provider" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-4">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-4">
                <FormField control={form.control} name="model" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>

}
